import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService
} from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { AuthStore } from '@ottobock/remote-update/domain';
import { filter } from 'rxjs';

@Component({
  imports: [RouterModule, MatButtonModule, MatDividerModule, MatIconModule, MatProgressSpinner],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit {
  msalService = inject(MsalService);
  msalBroadcastService = inject(MsalBroadcastService);
  msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  authStore = inject(AuthStore);
  isLoggingIn = computed(() => this.authStore.isLoggingIn());

  private redirectToLogin(): void {
    this.authStore.clearAuth();
    this.msalService.loginRedirect({
      ...this.msalGuardConfig.authRequest
    } as RedirectRequest);
  }

  ngOnInit() {
    this.msalService.handleRedirectObservable().subscribe({
      error: () => {
        this.redirectToLogin();
      }
    });

    this.msalBroadcastService.inProgress$.subscribe(status => {
      if (
        status === InteractionStatus.HandleRedirect ||
        status === InteractionStatus.Startup ||
        status === InteractionStatus.AcquireToken
      ) {
        this.authStore.setIsLoggingIn(true);
      } else if (status === InteractionStatus.None) {
        this.authStore.setIsLoggingIn(false);
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(filter(status => status === InteractionStatus.None))
      .subscribe(async () => {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length === 0) {
          this.redirectToLogin();
          return;
        }

        this.msalService.instance.setActiveAccount(accounts[0]);
        this.authStore.hydrateFromMsal();
        try {
          await this.authStore.acquireAccessTokenSilently(accounts[0]);
        } catch {
          this.authStore.logout();
        }
      });
  }
}
